<ng-container *transloco="let convert;">
  <!--
    buy_now_modal--all-in is the flag's hook in CSS. Every visual change M4.13 makes to this modal is
    gated on buy-allInPricingEnabled, including the ones that are pure styling, so the class rides on
    showDripBreakdown rather than being set unconditionally: this component is shared with every
    flag-off tenant and a bare rule here would restyle their modal too. Same shape as the bid modal's
    bid_modal--all-in.
  -->
  <app-modal [modalId]="modalId" class="buy_now_modal" [class.buy_now_modal--all-in]="showDripBreakdown">
    @if (isLoading) {
      <div class="loader-container">
        <app-spinner></app-spinner>
      </div>
    }
    @if (!isLoading && buyState === 'notBought') {
      <header class="modal__header">
        <h1 class="modal__title" [attr.id]="modalId + '-title'" tabindex="-1">
          {{ convert('vehicles.buyNowModal.textBuyNow') }}
        </h1>
      </header>
      @if (showWarning) {
        <app-vehicle-changed-warning></app-vehicle-changed-warning>
      }
      @if (errorMessage) {
        <p class="text-danger fw-bold" role="alert" aria-live="polite">{{convert('error.' + errorMessage) }}</p>
      }
      <div class="modal__content" >
        <!--
          M4.12: with buy-allInPricingEnabled on, ONE paragraph replaces all THREE of today's blocks
          -- the "Important: By selecting Buy Now ..." sentence, the legacy transportation sentence,
          and the separate Selling Disclosures line -- and it now sits BELOW the breakdown instead of
          above it. Comps: Figma 10360:9184 (with transportation) and 10360:9217 (without).

          This supersedes M4.2's approach here. M4.2 kept today's sentence and swapped two tokens
          inside it, because at that point no comp existed for the all-in wording; once the comp
          supplies a whole replacement sentence there is nothing left of the old one to swap into.

          The figure quoted is the breakdown's own TOTAL PURCHASE PRICE, transportation INCLUDED. The
          two comps above are the same modal differing only in whether a transportation fee exists,
          and the quoted figure moves with the total ($59,650 with, $59,300 without) rather than
          staying on the all-in price -- see modalTotalDue in the component.

          Flag off, all three blocks render exactly as today, in today's order.
        -->
        @if (showDripBreakdown) {
          <!--
            The comp's vehicle identification block (Figma 10360:9217): the title in bold with the VIN
            on the line beneath it, between the 'Buy Now' heading and the breakdown. Same block the bid
            modal gained in M4.11, for the same two reasons.

            It is inside this branch rather than beside it because flag-off tenants already name the
            vehicle -- their prose reads '... you agree to buy the 2017 Toyota Camry XLE - VIN 4T1BF...
            at the price of ...' -- so rendering this as well would state it twice. M4.12's all-in
            sentence dropped the vehicle and the VIN, which is why the comp lifts them out to here.

            The VIN keeps .stockwave-bid-buy-vin. Stockwave reads the VIN out of that hook and it
            existed only in the flag-off prose below, so with the flag on this modal currently exposes
            no VIN for the extension to find; this restores it.
          -->
          <p class="buy_now_vehicle">
            <span class="buy_now_vehicle__title">{{ titleSimple }}</span>
            <span class="buy_now_vehicle__vin">{{ convert('common.textVin') }} <span class="stockwave-bid-buy-vin">{{ vin }}</span></span>
          </p>
          <!--
            baseAmount is the RAW vehicle price. The breakdown adds the fee rows itself to reach the
            bold all-in headline, so binding allInBuyNowPrice here would double-count every one of them.

            showCents is false because the comp quotes whole dollars throughout -- '$59,300', not
            '$59,300.00'. It is passed explicitly rather than left to the input's default: the default
            is `true` and belongs to the component, while which of the two the modal shows is the
            comp's decision and therefore the host's to state. formatAllInCurrency matches it, so the
            binding sentence and the total row stay the same number written the same way.
          -->
          <app-modal-price-breakdown
            idPrefix="bnm_price"
            [pattern]="decomposedPattern"
            [baseAmount]="buyNowAmount"
            [feeRows]="dripFeeRows"
            [transportationFee]="dripTransportationFee"
            [showCents]="false"
            [translations]="dripBreakdownTranslations">
          </app-modal-price-breakdown>
          <br/>
          <!--
            The data-test-ids are deliberately the SAME in both branches rather than new ones for the
            all-in state: only one branch ever renders, and the automation suite looks for the info
            text and the transportation text by those names regardless of which copy is in place.
          -->
          <p class="modal__content--info" id="bnm_binding_text" data-test-id="app-buy-now-modal-e-modal-info-text">
            <span [innerHTML]="convert('vehicles.buyNowModal.msgBindingPurchaseAllInPartOne')"></span>
            <!--
              Split around the link rather than embedding an anchor in the translation string: the
              href is config-driven and routerLink cannot be applied through [innerHTML].

              No whitespace between the outlet and part two -- part two opens with '.', and a newline
              there would render 'Selling Disclosures .'
            -->
            <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container><span
              [innerHTML]="convert('vehicles.buyNowModal.msgBindingPurchaseAllInPartTwo', { amount: quotedPriceDisplay })"></span>
          </p>
          <br/>
          <!--
            The optional-transportation paragraph. It renders only inside this all-in branch, so the
            drip flag already applies through showDripBreakdown; within it the gate is the QUOTED FEE:
            showTransportationDisclosure is the same predicate the breakdown's transportation row uses,
            so the row and the sentence about avoiding it appear and disappear together. Kept although
            the comp does not show it -- a binding total that includes transportation needs the
            avoidability disclosure.
          -->
          @if (showTransportationDisclosure) {
            <p class="modal__content--info" id="bnm_transportation_text"
              data-test-id="app-buy-now-modal-e-modal-transportaion-text"
              [innerHTML]="convert('vehicles.buyNowModal.msgTransportationOptional')"></p>
            <br/>
          }
          <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
        } @else {
          <p class="modal__content--info" data-test-id="app-buy-now-modal-e-modal-info-text">
            <strong>{{buyNowModalTranslations.textImportant}}</strong>
            {{buyNowModalTranslations.textBySelecting}}
            <strong>{{buyNowModalTranslations.textBuyNow}}</strong>
            {{buyNowModalTranslations.textYouAgree}}
            {{title}} <span class="stockwave-bid-buy-vin">{{vin}}</span> {{buyNowModalTranslations.textAtPrice}}
            <span class="stockwave-bid-buy-amount">{{quotedPriceDisplay}}</span> {{buyNowModalTranslations.contentSelectingBuyNow}}
          </p>
          <br/>
          <p class="modal__content--info" data-test-id="app-buy-now-modal-e-modal-transportaion-text">
            {{buyNowModalTranslations.textTransportationService}}
          </p>
          <br/>
          <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
          @if (showSellingDisclosure) {
            <p>
              {{ convert('vehicles.buyNowModal.textSelectingBuyNow') }}
              <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container>.
            </p>
          }
        }
        </div>
        <div class="modal__action">
          <button
            id="bnm_buy_now_button"
            class="btn btn-primary buynow-btn stockwave-buy-button"
            (click)="buyNow()"
            data-track-elem="button"
            data-test-id="app-buy-now-modal-e-buy-now-button"
            data-track-name="Buy Now"
            allytmln='buyNowBuyAction'
            >
            {{ convert('vehicles.buyNowModal.textBuyNow') }}
          </button>
          <button
            id="bnm_cancel_button"
            class="btn btn-link cancel-btn bid-buttons"
            (click)="close()"
            data-test-id="app-buy-now-modal-e-close-button"
            data-track-elem="button"
            data-track-name="Cancel"
            data-track-trigger="close"
            >
            {{ convert('common.btnCancel') }}
          </button>
        </div>
        <app-modal-close (close)="close()" [identifier]="'bnm_close_x_button'"></app-modal-close>
      }
      @if (buyState === 'buying') {
        <app-action-submitting actionText="{{ convert('vehicles.buyNowModal.actionTextSubmitting') }}"></app-action-submitting>
      }
      @if (buyState === 'bought') {
        <app-action-submitted
          (close)="close()"
          actionText="{{ convert('vehicles.buyNowModal.actiontextSubmittedBuy') }}"
          [tenantName]="tenantName"
          (keydown.escape)="close()"
        ></app-action-submitted>
      }
    </app-modal>

    <!--
      The Selling Disclosures link, extracted in M4.12 so the legacy paragraph and the new binding
      sentence share ONE definition of it instead of two that can drift apart.

      The @else branch is not padding. showSellingDisclosure gates the disclosuresLinkEnabled flag,
      and with that flag off the binding sentence still has to read "... and accept our Selling
      Disclosures." -- the words stay, only the anchor goes. Without it part one would end mid-phrase
      on "our". The flag is named for the LINK, so dropping the link and keeping the noun is what it
      asks for. The legacy paragraph keeps its own outer @if, so nothing renders there either way.
    -->
    <ng-template #SellingDisclosuresLink>
      @if (showSellingDisclosure) {
        <strong
          ><a
          [attr.aria-label]="convert('vehicles.buyNowModal.ariaLabelSellerDisclosure')"
          target="_blank"
          rel="opener"
          routerLink="{{ sellingDisclosureUrl }}"
          data-track-elem="button"
          data-test-id="app-buy-now-modal-e-selling-disclosures-link"
          data-track-name="Selling Disclosures"
          aria-label="Open Selling Disclosures"
          >{{ convert('vehicles.buyNowModal.ariaLabelSellerDisclosure') | titlecase}}</a
          ></strong
          >
      } @else {
        <strong>{{ convert('vehicles.buyNowModal.ariaLabelSellerDisclosure') | titlecase}}</strong>
      }
    </ng-template>

    <ng-template #financeSourceFeeMessage>
      @if (isFinanceSourceFeeIncluded()) {
        <p aria-labelledby="bid-modal-finance-source-fee-text" [innerHTML]="convert('vehicles.bidModal.textFinanceSourceFee',{name: financeSource.financeSourceName, amount: financeSource.feeAmount})"></p>
      }
    </ng-template>

  </ng-container>
